/**
 * Pure functional game loop engine for Rogue's Cog.
 *
 * Input state -> output state. Deterministic given same seed + inputs.
 * Zero browser dependencies. JSON-serializable state (except PRNG internals).
 */

import { CONFIG } from './config.js';
import {
  type BoardState,
  type MutationCard,
  type Bubble,
  type GameState,
  type GameMode,
  type GameStatus,
  type ActiveMutation,
  type MutationDraft,
  BubbleColor,
  ALL_BUBBLE_COLORS,
  addBubble,
  removeBubbles,
  getBubble,
} from './types.js';
import type { HexCoord, Vec2 } from './grid/hex.js';
import {
  hexToPixel,
  hexNeighbors,
  coordToKey,
  hexDistance,
} from './grid/hex.js';
import { findColorCluster, findFloatingClusters } from './grid/cluster.js';
import {
  createSpatialHash,
  insert as spatialInsert,
} from './grid/spatial-hash.js';
import { createRNG, type PRNG } from './rng.js';
import { computeTrajectory } from './physics/trajectory.js';
import { snapToGrid } from './physics/snap.js';
import { calculateScore, type ScoringEvent } from './scoring/score.js';
import { updateMultiplier } from './scoring/multiplier.js';
import { generateDraft } from './mutations/draft.js';
import { applyMutation } from './mutations/apply.js';
import { MutationType } from './mutations/registry.js';
import { enhancementMagnitude, nextEnhancementLevel } from './mutations/levels.js';
import { pickCascadeCoilTargets } from './mutations/types/cascade-coil.js';

/**
 * Build a spatial hash from the current board state for collision detection.
 */
function buildSpatialHash(board: BoardState) {
  const hash = createSpatialHash(CONFIG.CELL_SIZE * 2);
  for (const [, bubble] of board.bubbles) {
    const pos = hexToPixel(bubble.coord, CONFIG.CELL_SIZE);
    spatialInsert(hash, bubble, pos);
  }
  return hash;
}

/**
 * Generate the next N bubble colors from the RNG, using only active colors.
 */
function generateNextColors(rng: PRNG, activeColors: readonly BubbleColor[], count: number): BubbleColor[] {
  const colors: BubbleColor[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(rng.pick(activeColors));
  }
  return colors;
}

/**
 * Create a new board with the given dimensions and randomly filled rows.
 */
export function createBoard(
  width: number,
  rows: number,
  rng: PRNG,
  numColors: number,
): BoardState {
  const activeColors = ALL_BUBBLE_COLORS.slice(0, numColors);
  let board: BoardState = {
    bubbles: new Map(),
    width,
    ceilingRow: 0,
    activeColors: [...activeColors],
  };

  for (let r = 0; r < rows; r++) {
    for (let q = 0; q <= width; q++) {
      const color = rng.pick(activeColors);
      board = addBubble(board, { q, r }, color);
    }
  }

  return board;
}

/**
 * Create a new game state from a seed string.
 */
export function createGame(
  seed: string,
  mode: GameMode,
  isTutorial: boolean = false,
  initialRows: number = CONFIG.INITIAL_ROWS,
  gamesPlayed: number = 0,
): GameState {
  const rng = createRNG(seed);
  const effectiveInitialRows = mode === 'daily' ? CONFIG.DAILY_INITIAL_ROWS : initialRows;
  const board = createBoard(
    CONFIG.BOARD_COLUMNS,
    effectiveInitialRows,
    rng,
    CONFIG.INITIAL_ACTIVE_COLORS,
  );
  const nextColors = generateNextColors(rng, board.activeColors, 3);

  return {
    board,
    projectile: null,
    nextColors,
    heldBubble: null,
    score: 0,
    multiplierX10: 10,
    turnNumber: 0,
    seed,
    rng,
    pendingDraft: null,
    activeMutations: [],
    enhancementLevels: {},
    turnsUntilDraft: CONFIG.FIRST_DRAFT_TURN,
    status: 'aiming' as GameStatus,
    mode,
    isTutorial,
    tutorialStep: 0,
    nearFailureCount: 0,
    lastScoreBreakdown: null,
    gamesPlayed,
    shotClockMs: null,
    shotClockStartedAt: null,
    draftCount: 0,
    gameStartedAt: Date.now(),
    descentElapsedMs: 0,
    lastDescentAt: Date.now(),
    descentOffset: 0,
    lastTheftAt: Date.now(),
    endReason: null,
  };
}

/**
 * Compute the game-over row threshold adjusted for descent offset.
 * A bubble with coord.r >= this value triggers game over.
 *
 * Derivation: a bubble at hex row r renders at visual y =
 * descentOffset + (r - 1) * ROW_HEIGHT_PX (the -1 is because the renderer
 * shifts the board container up by ROW_HEIGHT_PX so new rows scroll in from
 * offscreen). We want the smallest integer r such that the bubble's center
 * has reached the line at y = SHOOTER_Y - AIM_OFFSET_Y:
 *   (r - 1) * ROW_HEIGHT_PX + descentOffset >= line_y
 *   r >= (line_y - descentOffset) / ROW_HEIGHT_PX + 1
 * The smallest such integer is ceil(A) + 1 where A is the quotient above.
 * Using floor would trigger a row too early when A is not an exact integer,
 * leaving the lowest gear visibly above the line when game over fires.
 */
/**
 * Turns until the next draft after `draftCount` drafts have occurred.
 * Grows by one turn every DRAFT_INTERVAL_STEP drafts: 7, 7, 7, 8, 8, 8, 9, ...
 */
export function nextDraftInterval(draftCount: number): number {
  return CONFIG.FIRST_DRAFT_TURN + Math.floor(draftCount / CONFIG.DRAFT_INTERVAL_STEP);
}

function getGameOverRow(descentOffset: number): number {
  return Math.ceil((CONFIG.SHOOTER_Y - CONFIG.AIM_OFFSET_Y - descentOffset) / ROW_HEIGHT_PX) + 1;
}

/**
 * Check if a coord is in the near-failure zone (close to the bottom).
 */
function isNearFailure(coord: HexCoord, descentOffset: number): boolean {
  return coord.r >= getGameOverRow(descentOffset) - CONFIG.NEAR_FAILURE_ROWS;
}

/**
 * True when any bubble on the board currently sits in the near-failure
 * zone — i.e. the same "within NEAR_FAILURE_ROWS of the game-over line"
 * band that grants the +1.0x shot bonus in `updateMultiplier`. Exposed
 * so the music controller can mirror that scoring window exactly.
 */
export function isBoardNearFailure(state: GameState): boolean {
  const threshold = getGameOverRow(state.descentOffset) - CONFIG.NEAR_FAILURE_ROWS;
  for (const [, bubble] of state.board.bubbles) {
    if (bubble.coord.r >= threshold) return true;
  }
  return false;
}

/**
 * Check if the game is over (any bubble's visual position is at/past the shooter).
 */
function checkGameOver(board: BoardState, descentOffset: number): boolean {
  const maxRow = getGameOverRow(descentOffset);
  for (const [, bubble] of board.bubbles) {
    if (bubble.coord.r >= maxRow) {
      return true;
    }
  }
  return false;
}

/**
 * Count same-color neighbors of a coord (for setup shot detection).
 */
export function countSameColorNeighbors(board: BoardState, coord: HexCoord, color: BubbleColor): number {
  let count = 0;
  for (const neighbor of hexNeighbors(coord)) {
    const bubble = getBubble(board, neighbor);
    if (bubble && (bubble.color === color || bubble.isWild)) {
      count++;
    }
  }
  return count;
}

/**
 * Get all coords within a given hex radius of a center coord (for bomb explosion).
 */
function getCoordsInRadius(center: HexCoord, radius: number, board: BoardState): HexCoord[] {
  const result: HexCoord[] = [];
  for (const [, bubble] of board.bubbles) {
    if (hexDistance(center, bubble.coord) <= radius) {
      result.push(bubble.coord);
    }
  }
  return result;
}

/**
 * turnsRemaining sentinel for shot-counted buffs (Narrow Spectrum, Wildcard
 * Gear). They expire on `shotsRemaining`, never on the per-turn decrement.
 */
const SHOT_COUNTED_TURNS_SENTINEL = 999;

/** True for ActiveMutation entries that expire on shots, not turns. */
function isShotCounted(m: ActiveMutation): boolean {
  return m.shotsRemaining !== undefined;
}

/** Decrement one shot from `type`'s entry, dropping it at zero. */
function consumeShot(mutations: ReadonlyArray<ActiveMutation>, type: MutationType): ActiveMutation[] {
  return mutations
    .map(m => (m.type === type && m.shotsRemaining !== undefined
      ? { ...m, shotsRemaining: m.shotsRemaining - 1 }
      : m))
    .filter(m => !(m.type === type && (m.shotsRemaining ?? 0) <= 0));
}

/** Compute the top-N on-board colors by cell count, tiebreaking by enum order. */
function topBoardColors(board: BoardState, n: number): BubbleColor[] {
  const counts = new Map<BubbleColor, number>();
  for (const [, bubble] of board.bubbles) {
    if (bubble.isObstacle) continue;
    counts.set(bubble.color, (counts.get(bubble.color) ?? 0) + 1);
  }
  const candidates = board.activeColors.slice();
  candidates.sort((a, b) => {
    const diff = (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
    if (diff !== 0) return diff;
    return ALL_BUBBLE_COLORS.indexOf(a) - ALL_BUBBLE_COLORS.indexOf(b);
  });
  return candidates.slice(0, Math.min(n, candidates.length));
}

/** Returns the color palette to draw the next bubble from, honoring Narrow Spectrum. */
function nextDrawColors(
  activeMutations: ReadonlyArray<ActiveMutation>,
  fallback: readonly BubbleColor[],
): readonly BubbleColor[] {
  const ns = activeMutations.find(
    m => m.type === MutationType.NarrowSpectrum &&
      (m.shotsRemaining ?? 0) > 0 &&
      m.payload?.colors &&
      m.payload.colors.length > 0,
  );
  return ns?.payload?.colors ?? fallback;
}

/**
 * Turn-counted beneficial buffs. Duration comes from the enhancement level
 * ladder (mutations/levels.ts). Re-drafting one while it is still running
 * adds the new duration onto what is left.
 */
const TIMER_MUTATIONS: readonly MutationType[] = [
  MutationType.ScoreFrenzy,
  MutationType.SlowClockwork,
  MutationType.LubricatedBearings,
  MutationType.GovernorLock,
];

/**
 * Shot-counted beneficial buffs (expire on `shotsRemaining`). Re-drafting
 * adds the new shots onto what is left.
 */
const SHOT_COUNTED_MUTATIONS: readonly MutationType[] = [
  MutationType.WildcardGear,
  MutationType.NarrowSpectrum,
];

/**
 * One-shot beneficial mutations that attach a single-use ActiveMutation entry
 * (carrying its `level`) when drafted. Consumed (filtered out) on the fire
 * that uses them via `consumedOneShotTypes`, regardless of pop outcome.
 * Re-drafting while still armed re-arms at the new level.
 */
const ONE_SHOT_BENEFICIAL_MUTATIONS: readonly MutationType[] = [
  MutationType.BubbleBomb,
  MutationType.CascadeCoil,
];

/** Level stamped on an armed one-shot entry (Mk. I when absent). */
function armedLevel(mutations: ReadonlyArray<ActiveMutation>, type: MutationType): number {
  const entry = mutations.find(m => m.type === type && m.turnsRemaining > 0);
  return entry?.level ?? 1;
}

/**
 * The main game step: fire a bubble at the given angle.
 *
 * Pure function: returns a new GameState without mutating the input.
 */
export function fireBubble(
  state: GameState,
  angle: number,
  didSwapHeld?: boolean,
): GameState {
  if (state.status === 'game-over') {
    return state;
  }

  // (a) Quantize angle to AIM_ANGLE_PRECISION decimals
  const precision = CONFIG.AIM_ANGLE_PRECISION;
  const factor = 10 ** precision;
  const quantizedAngle = Math.round(angle * factor) / factor;

  // Clamp angle to valid range
  const clampedAngle = Math.max(CONFIG.AIM_ANGLE_MIN, Math.min(CONFIG.AIM_ANGLE_MAX, quantizedAngle));

  // (b) Handle hold/swap logic
  let currentColor = state.nextColors[0]!;
  let newNextColors = [...state.nextColors];
  let newHeldBubble = state.heldBubble;

  if (didSwapHeld && state.heldBubble !== null) {
    // Swap: current shot becomes the held bubble, held becomes current
    const temp = currentColor;
    currentColor = state.heldBubble;
    newHeldBubble = temp;
  } else if (didSwapHeld && state.heldBubble === null) {
    // First hold: put current into hold, use next one
    newHeldBubble = currentColor;
    currentColor = newNextColors[1]!;
    newNextColors = [newNextColors[1]!, newNextColors[2]!, ...newNextColors.slice(3)];
  }

  // Check for active mutations affecting the shot
  const isBombShot = state.activeMutations.some(m => m.type === MutationType.BubbleBomb && m.turnsRemaining > 0);
  const isWildShot = state.activeMutations.some(m => m.type === MutationType.WildcardGear && (m.shotsRemaining ?? 0) > 0);
  const isFrenzyActive = state.activeMutations.some(m => m.type === MutationType.ScoreFrenzy && m.turnsRemaining > 0);
  const isLubricated = state.activeMutations.some(m => m.type === MutationType.LubricatedBearings && m.turnsRemaining > 0);

  // Cascade Coil rides on a normal colored shot. A Powder Keg shot leaves
  // the coil armed for the next non-bomb shot instead of wasting it.
  const isCascadeShot = !isBombShot &&
    state.activeMutations.some(m => m.type === MutationType.CascadeCoil && m.turnsRemaining > 0);
  const bombLevel = armedLevel(state.activeMutations, MutationType.BubbleBomb);
  const cascadeLevel = armedLevel(state.activeMutations, MutationType.CascadeCoil);

  // One-shot mutations consumed by firing, regardless of pop outcome. Filtered
  // from activeMutations during the rebuild step below.
  const consumedOneShotTypes: MutationType[] = [];
  if (isBombShot) consumedOneShotTypes.push(MutationType.BubbleBomb);
  if (isCascadeShot) consumedOneShotTypes.push(MutationType.CascadeCoil);

  /** Reap consumed one-shots and spend one Wildcard shot if this fire used it. */
  const settleShotBuffs = (mutations: ReadonlyArray<ActiveMutation>): ActiveMutation[] => {
    const kept = mutations.filter(m => !consumedOneShotTypes.includes(m.type as MutationType));
    return isWildShot ? consumeShot(kept, MutationType.WildcardGear) : kept;
  };

  // A "pure swap" is a hold-swap call where the player already had a held
  // gear — the current projectile is just exchanged with the held one. Per
  // the Narrow Spectrum spec (Packet 6 §Tests): swapping with the held
  // bubble does NOT count as a fired shot for NS's per-shot counter —
  // only actually firing a projectile decrements. The first hold (when
  // heldBubble === null) still fires a projectile of the next-queued color,
  // so it IS a real shot. Pure swaps are the only exception.
  const isPureSwap = didSwapHeld === true && state.heldBubble !== null;
  const didFireProjectile = !isPureSwap;

  // (c) Compute trajectory
  const spatialHash = buildSpatialHash(state.board);
  const boardCenterX = CONFIG.CELL_SIZE * (CONFIG.BOARD_COLUMNS * 2 + 1) / 2;
  // Origin Y in hex space: the cannon is at SHOOTER_Y on screen, and the board
  // container is at y = descentOffset - ROW_HEIGHT. Converting screen to hex:
  // hex_y = screen_y - boardContainer_y = SHOOTER_Y - (descentOffset - ROW_HEIGHT)
  const ROW_HEIGHT = CONFIG.CELL_SIZE * 1.732;
  const origin: Vec2 = {
    x: boardCenterX,
    y: CONFIG.SHOOTER_Y - state.descentOffset + ROW_HEIGHT,
  };
  const trajectory = computeTrajectory(origin, clampedAngle, state.board, spatialHash);

  // (d) Place bubble on board at snap position.
  // Use the raw collision pixel position for better snap accuracy.
  let snapCoord: HexCoord | null = null;
  if (trajectory.hitPosition) {
    snapCoord = snapToGrid(trajectory.hitPosition, state.board);
  } else if (trajectory.hitCoord) {
    snapCoord = snapToGrid(hexToPixel(trajectory.hitCoord, CONFIG.CELL_SIZE), state.board);
  }

  // If still no valid position, the shot goes nowhere meaningful — still advance turn
  if (!snapCoord) {
    const advTurn = state.turnNumber + 1;
    let advBoard = state.board;

    // Still handle color unlocks
    let advColors = [...advBoard.activeColors];
    for (const ut of CONFIG.COLOR_UNLOCK_TURNS) {
      if (advTurn === ut && advColors.length < CONFIG.MAX_COLORS) {
        const nc = ALL_BUBBLE_COLORS[advColors.length];
        if (nc) advColors = [...advColors, nc];
      }
    }
    if (advColors.length !== advBoard.activeColors.length) {
      advBoard = { ...advBoard, activeColors: advColors };
    }

    // Still handle draft timing
    let advTurnsUntilDraft = state.turnsUntilDraft - 1;
    let advPendingDraft = state.pendingDraft;
    let advStatus: GameStatus = 'aiming';
    let advDraftCount = state.draftCount;
    let advActiveMutations = settleShotBuffs(state.activeMutations);
    let advDescentOffset = state.descentOffset;
    if (advTurnsUntilDraft <= 0) {
      const { applied, draft } = generateDraft(state.rng, advTurn, state.enhancementLevels);
      if (applied) {
        ({ board: advBoard, descentOffset: advDescentOffset } =
          applyHarmfulCard(advBoard, advDescentOffset, draft.harmful, state));
      }
      advDraftCount++;
      advTurnsUntilDraft = nextDraftInterval(advDraftCount);
      // Defensive: generateDraft always fills a hand, but never open an empty draft screen.
      if (draft.options.length > 0) {
        advPendingDraft = draft;
        advStatus = 'drafting';
      }
    }

    // Advance next colors (honor Narrow Spectrum if active)
    const advDrawPool = nextDrawColors(advActiveMutations, advBoard.activeColors);
    if (!didSwapHeld || state.heldBubble !== null) {
      newNextColors = [...newNextColors.slice(1), state.rng.pick(advDrawPool)];
    } else {
      newNextColors = [...newNextColors, state.rng.pick(advDrawPool)];
    }
    while (newNextColors.length < 3) {
      newNextColors.push(state.rng.pick(advDrawPool));
    }

    // Decrement Narrow Spectrum shotsRemaining (a no-snap still counts as a
    // fired shot). Pure swaps (exchanging with an existing held gear) are NOT
    // a fired shot and do not decrement.
    if (didFireProjectile) {
      advActiveMutations = advActiveMutations
        .map(m => (m.type === MutationType.NarrowSpectrum && m.shotsRemaining !== undefined
          ? { ...m, shotsRemaining: m.shotsRemaining - 1 }
          : m))
        .filter(m => !(m.type === MutationType.NarrowSpectrum && (m.shotsRemaining ?? 0) <= 0));
    }

    // A Row Inject can push the tower past the failure line.
    const advOverflow =
      advDescentOffset !== state.descentOffset && checkGameOver(advBoard, advDescentOffset);

    return {
      ...state,
      board: advBoard,
      descentOffset: advDescentOffset,
      turnNumber: advTurn,
      nextColors: newNextColors,
      heldBubble: newHeldBubble,
      turnsUntilDraft: advTurnsUntilDraft,
      pendingDraft: advPendingDraft,
      activeMutations: advActiveMutations,
      draftCount: advDraftCount,
      status: advOverflow ? 'game-over' : advStatus,
      endReason: advOverflow ? 'overflow' : state.endReason,
    };
  }

  let board = addBubble(state.board, snapCoord, currentColor, {
    isBomb: isBombShot,
    isWild: isWildShot,
  });

  let clustersPopped = 0;
  let bubblesDropped = 0;
  let poppedCoords: HexCoord[] = [];

  // (e) Check for bomb bubble -> destroy radius
  if (isBombShot) {
    const blastRadius = enhancementMagnitude(MutationType.BubbleBomb, bombLevel);
    const blastCoords = getCoordsInRadius(snapCoord, blastRadius, board);
    clustersPopped = blastCoords.length;
    board = removeBubbles(board, blastCoords);
    poppedCoords = blastCoords;
  } else if (!isCascadeShot) {
    // (f) Find color cluster -> pop if >= MIN_CLUSTER_SIZE. Cascade Coil is
    // its own thing: the electric orb replaces whatever queue colour was
    // next, but on landing it must NOT trigger the normal colour-match pop
    // — that would play a plain cluster-pop animation on the coil's incident
    // colour before the strike even fires. All touched colours (including
    // the coil's incident one) get stripped by the cascade instead.
    const cluster = findColorCluster(board, snapCoord);
    if (cluster.length >= CONFIG.MIN_CLUSTER_SIZE) {
      clustersPopped = cluster.length;
      board = removeBubbles(board, cluster);
      poppedCoords = cluster;
    }
  }

  // Cascade Coil: on landing (match or not), every color the shot touches
  // sheds random gears elsewhere on the board — all of them at Mk. V.
  // Resolved against the post-pop board so the primary cluster isn't
  // double-counted. Must be the first RNG consumer in this function so the
  // renderer's forked-PRNG prediction lands on the same targets.
  //
  // The cascade shot itself is a color-agnostic electric orb (see the
  // cannon/projectile renderer) — after it fires the cascade, the orb
  // dissolves rather than settling as a gear at snapCoord. Idempotent w.r.t.
  // the primary-cluster branch, which may have already removed snapCoord.
  let coilColorsHit = 0;
  if (isCascadeShot) {
    const landedBoard = addBubble(state.board, snapCoord, currentColor, { isWild: isWildShot });
    const coil = pickCascadeCoilTargets(landedBoard, board, snapCoord, cascadeLevel, state.rng);
    if (coil.removed.length > 0) {
      clustersPopped += coil.removed.length;
      board = removeBubbles(board, coil.removed);
      poppedCoords.push(...coil.removed);
      coilColorsHit = coil.colorsHit;
    }
    board = removeBubbles(board, [snapCoord]);
  }

  // (g) Check setup shot (2+ same-color neighbors if no pop)
  const isSetupShot = clustersPopped === 0 &&
    countSameColorNeighbors(board, snapCoord, currentColor) >= 2;

  // (h) Find and remove floating clusters — only after a pop.
  // Placing a bubble can never disconnect anything; only removing
  // bubbles (popping a cluster or bomb blast) can create floaters.
  let floatingCoords: HexCoord[] = [];
  if (clustersPopped > 0) {
    floatingCoords = findFloatingClusters(board);
    if (floatingCoords.length > 0) {
      bubblesDropped = floatingCoords.length;
      board = removeBubbles(board, floatingCoords);
    }
  }

  // Chain bonus (chainDepth² × 25): one link per touched color the coil
  // actually stripped. Bounded by the palette size, so it can't run away.
  const chainDepth = coilColorsHit;

  // (i) Check near-failure
  const nearFailure = isNearFailure(snapCoord, state.descentOffset);

  const governorLocked = state.activeMutations.some(
    m => m.type === MutationType.GovernorLock && m.turnsRemaining > 0,
  );

  // (j) Build ScoringEvent, calculate score
  const scoringEvent: ScoringEvent = {
    clustersPopped,
    bubblesDropped,
    bankShots: trajectory.bounces,
    chainDepth,
    nearFailure,
    isSetupShot,
    frenzyActive: isFrenzyActive,
    governorLocked,
    bankCoefX10: isLubricated ? 25 : 15,
  };

  const scoreResult = calculateScore(scoringEvent, state.multiplierX10);
  const newScore = state.score + scoreResult.points;

  // (k) Update multiplier
  const newMultiplierX10 = updateMultiplier(state.multiplierX10, scoringEvent, state.gamesPlayed);

  // (l) Handle color unlocks at COLOR_UNLOCK_TURNS
  const newTurnNumber = state.turnNumber + 1;
  let activeColors = [...board.activeColors];
  for (const unlockTurn of CONFIG.COLOR_UNLOCK_TURNS) {
    if (newTurnNumber === unlockTurn && activeColors.length < CONFIG.MAX_COLORS) {
      const nextColor = ALL_BUBBLE_COLORS[activeColors.length];
      if (nextColor) {
        activeColors = [...activeColors, nextColor];
      }
    }
  }
  if (activeColors.length !== board.activeColors.length) {
    board = { ...board, activeColors };
  }

  // (m) Decrement active mutation timers, remove expired + one-shots consumed this fire.
  // Narrow Spectrum uses shotsRemaining instead and decrements once per fired
  // shot here — but only if an actual projectile was fired (a pure held-swap,
  // where the player just exchanges with an existing held gear, does not count).
  let activeMutations: ActiveMutation[] = settleShotBuffs(state.activeMutations)
    .map(m => {
      if (m.type === MutationType.NarrowSpectrum && m.shotsRemaining !== undefined) {
        return didFireProjectile
          ? { ...m, shotsRemaining: m.shotsRemaining - 1 }
          : m;
      }
      // Shot-counted buffs expire on shots; armed one-shots expire only by
      // being used (e.g. Cascade Coil survives a Powder Keg shot).
      if (isShotCounted(m) || ONE_SHOT_BENEFICIAL_MUTATIONS.includes(m.type as MutationType)) return m;
      return { ...m, turnsRemaining: m.turnsRemaining - 1 };
    })
    .filter(m => (isShotCounted(m) ? (m.shotsRemaining ?? 0) > 0 : m.turnsRemaining > 0));

  // (n) Check if draft is due
  let turnsUntilDraft = state.turnsUntilDraft - 1;
  let pendingDraft: MutationDraft | null = state.pendingDraft;
  let newStatus: GameStatus = 'aiming';
  let draftCount = state.draftCount;
  let descentOffset = state.descentOffset;

  if (turnsUntilDraft <= 0) {
    const { applied, draft } = generateDraft(state.rng, newTurnNumber, state.enhancementLevels);
    // Auto-apply the harmful card if the roll succeeded
    if (applied) {
      ({ board, descentOffset } = applyHarmfulCard(board, descentOffset, draft.harmful, state));
    }
    draftCount++;
    // Interval steps up every DRAFT_INTERVAL_STEP drafts (7, 7, 7, 8, 8, 8, 9, ...)
    turnsUntilDraft = nextDraftInterval(draftCount);
    // Defensive: generateDraft always fills a hand, but never open an empty draft screen.
    if (draft.options.length > 0) {
      pendingDraft = draft;
      newStatus = 'drafting';
    }
  }

  // (p) Check game over. Overflow (ceiling-into-shooter) takes priority over
  // shot-budget exhaustion: a player who runs out of shots AND overflows on
  // the same tick still counts as defeated, not victorious.
  let endReason: GameState['endReason'] = null;
  if (checkGameOver(board, descentOffset)) {
    newStatus = 'game-over';
    endReason = 'overflow';
  } else if (state.mode === 'daily' && newTurnNumber >= CONFIG.DAILY_SHOT_BUDGET) {
    newStatus = 'game-over';
    endReason = 'shots-exhausted';
  }

  // (q) Advance next colors queue (honor Narrow Spectrum after step (m) decrement)
  const drawPool = nextDrawColors(activeMutations, board.activeColors);
  if (!didSwapHeld || state.heldBubble !== null) {
    newNextColors = [...newNextColors.slice(1), state.rng.pick(drawPool)];
  } else {
    newNextColors = [...newNextColors, state.rng.pick(drawPool)];
  }

  // Ensure we always have 3 colors in the queue
  while (newNextColors.length < 3) {
    newNextColors.push(state.rng.pick(drawPool));
  }

  // (r) Return new GameState
  return {
    ...state,
    board,
    descentOffset,
    projectile: null,
    nextColors: newNextColors,
    heldBubble: newHeldBubble,
    score: newScore,
    multiplierX10: newMultiplierX10,
    turnNumber: newTurnNumber,
    pendingDraft,
    activeMutations,
    turnsUntilDraft,
    status: newStatus,
    nearFailureCount: state.nearFailureCount + (nearFailure ? 1 : 0),
    lastScoreBreakdown: scoreResult.breakdown,
    shotClockMs: null,
    shotClockStartedAt: null,
    draftCount,
    endReason,
  };
}

/**
 * Apply the player's beneficial draft choice. Does NOT fire a shot.
 * Returns the updated state with status back to 'aiming'.
 */
export function applyDraftChoice(state: GameState, choiceIndex: number): GameState {
  if (state.status !== 'drafting' || !state.pendingDraft) return state;

  const chosenCard = state.pendingDraft.options[choiceIndex];
  if (!chosenCard) return state;

  let board = applyMutation(state.board, chosenCard, state.rng);
  // Drop any floating gears the chosen mutation may have created (or that
  // were already floating after the harmful auto-apply above). Keeps the
  // board topologically consistent before the player's next shot.
  const floatingAfterChoice = findFloatingClusters(board);
  if (floatingAfterChoice.length > 0) {
    board = removeBubbles(board, floatingAfterChoice);
  }
  let activeMutations = [...state.activeMutations];
  let nextColors = state.nextColors;

  // Enhancement level: this pick's Mk. number (picks so far + 1, capped).
  const type = chosenCard.type as MutationType;
  const level = nextEnhancementLevel(state.enhancementLevels, type);
  const magnitude = enhancementMagnitude(type, level);
  const enhancementLevels = { ...state.enhancementLevels, [type]: level };

  // Turn-counted buffs: one entry per type; re-drafting adds onto what's left.
  if (TIMER_MUTATIONS.includes(type)) {
    const remaining = activeMutations.find(m => m.type === type)?.turnsRemaining ?? 0;
    activeMutations = [
      ...activeMutations.filter(m => m.type !== type),
      { type, turnsRemaining: remaining + magnitude },
    ];
  }

  // One-shot beneficials attach a single-use ActiveMutation entry carrying
  // the level. It's reaped by `consumedOneShotTypes` on the fire that uses
  // it. turnsRemaining: 1 is a "has-entry-and-is-live" marker; the per-turn
  // timer decrement would expire it too but the one-shot filter always wins
  // first. Re-drafting while still armed re-arms at the new level.
  if (ONE_SHOT_BENEFICIAL_MUTATIONS.includes(type)) {
    activeMutations = [
      ...activeMutations.filter(m => m.type !== type),
      { type, turnsRemaining: 1, level },
    ];
  }

  // Shot-counted buffs: re-drafting adds shots onto what's left.
  if (SHOT_COUNTED_MUTATIONS.includes(type)) {
    const existing = activeMutations.find(m => m.type === type);
    const remaining = existing?.shotsRemaining ?? 0;
    const entry: ActiveMutation = {
      type,
      turnsRemaining: SHOT_COUNTED_TURNS_SENTINEL,
      shotsRemaining: remaining + magnitude,
    };
    if (type === MutationType.NarrowSpectrum) {
      // Palette re-narrows to the current top-3 on every pick.
      const colors = topBoardColors(board, 3);
      activeMutations = [
        ...activeMutations.filter(m => m.type !== type),
        { ...entry, payload: { colors } },
      ];
      if (colors.length > 0) {
        nextColors = nextColors.map(() => state.rng.pick(colors));
      }
    } else {
      activeMutations = [...activeMutations.filter(m => m.type !== type), entry];
    }
  }

  // Check game over after mutation (e.g., row inject could push bubbles down)
  const overflow = checkGameOver(board, state.descentOffset);
  const newStatus: GameStatus = overflow ? 'game-over' : 'aiming';

  return {
    ...state,
    board,
    nextColors,
    activeMutations,
    enhancementLevels,
    pendingDraft: null,
    status: newStatus,
    endReason: overflow ? 'overflow' : state.endReason,
  };
}

/**
 * Ceiling descent tick — injects a new row from the top if enough time has
 * elapsed. The interval between injections starts at DESCENT_INITIAL_INTERVAL_MS
 * and shrinks linearly to DESCENT_MIN_INTERVAL_MS over DESCENT_RAMP_DURATION_MS.
 *
 * Returns null if no descent happened, or the new state if a row was injected.
 */
/** Row height in pixels (CELL_SIZE * sqrt(3)). */
const ROW_HEIGHT_PX = CONFIG.CELL_SIZE * 1.732;

/**
 * Count visible non-obstacle bubbles as row-equivalents
 * ((bubbles in viewport) / BOARD_COLUMNS). A bubble at hex row r renders at
 * visual y = descentOffset + (r - 1) * ROW_HEIGHT_PX; we include anything at
 * y >= 0 (buffer rows above the ceiling render at negative y and don't count).
 */
export function countVisibleColoredRows(board: BoardState, descentOffset: number): number {
  let visibleCount = 0;
  for (const [, bubble] of board.bubbles) {
    if (bubble.isObstacle) continue;
    const visualY = descentOffset + (bubble.coord.r - 1) * ROW_HEIGHT_PX;
    if (visualY >= 0 && visualY <= CONFIG.BOARD_HEIGHT_PX) {
      visibleCount++;
    }
  }
  return visibleCount / CONFIG.BOARD_COLUMNS;
}

/**
 * Linear speedup multiplier based on how full the playfield is. Empty board
 * -> MAX_MULTIPLIER, >= THRESHOLD row-equivalents -> 1x. Keeps skilled
 * players under pressure after clearing the board without punishing players
 * who are already near failure.
 */
export function computeScrollMultiplier(visibleRows: number): number {
  const { DESCENT_SPEEDUP_THRESHOLD_ROWS: threshold, DESCENT_SPEEDUP_MAX_MULTIPLIER: max } = CONFIG;
  const t = Math.min(visibleRows / threshold, 1);
  return max - (max - 1) * t;
}

/**
 * Ceiling descent tick — smoothly advances the descent offset each frame.
 * When the offset reaches a full row height, injects a new row and resets.
 *
 * The speed (pixels/ms) ramps from slow to fast over DESCENT_RAMP_DURATION_MS
 * of descent-active time (see `descentElapsedMs`),
 * then is multiplied by a board-state-dependent factor (see
 * computeScrollMultiplier) so an emptier board scrolls in new rows faster.
 * Returns null if nothing changed (game over or paused states).
 */
export function applyDescentTick(state: GameState, now: number): GameState | null {
  if (state.status === 'game-over') return null;

  const paused = state.activeMutations.some(
    m => m.type === MutationType.SlowClockwork && m.turnsRemaining > 0,
  );
  if (state.status === 'drafting' || paused) {
    return { ...state, lastDescentAt: now };
  }

  const dtMs = now - state.lastDescentAt;
  if (dtMs <= 0) return null;

  // Compute current descent speed (pixels per ms). The ramp runs on
  // descent-active time, not wall-clock, so halts don't advance it.
  const elapsed = state.descentElapsedMs + dtMs;
  const rampT = Math.min(elapsed / CONFIG.DESCENT_RAMP_DURATION_MS, 1);
  const interval = CONFIG.DESCENT_INITIAL_INTERVAL_MS -
    rampT * (CONFIG.DESCENT_INITIAL_INTERVAL_MS - CONFIG.DESCENT_MIN_INTERVAL_MS);
  const visibleRows = countVisibleColoredRows(state.board, state.descentOffset);
  const scrollMultiplier = computeScrollMultiplier(visibleRows);
  const modeMultiplier = state.mode === 'daily' ? CONFIG.DAILY_DESCENT_MULTIPLIER : 1;
  const speedPxPerMs = (ROW_HEIGHT_PX / interval) * scrollMultiplier * modeMultiplier;

  const newOffset = state.descentOffset + dtMs * speedPxPerMs;
  return { ...advanceDescentTo(state, newOffset), descentElapsedMs: elapsed, lastDescentAt: now };
}

/**
 * Scroll the tower to `newOffset`, generating any rows the scroll reveals and
 * ending the run if the board now crosses the failure line.
 *
 * This is the wall-clock-free half of the descent: `applyDescentTick` decides
 * how far to scroll; a replay feeds back the offset recorded with each frame.
 * A row's colors come from a PRNG keyed on (seed, row) rather than from
 * `state.rng`, so how many rows have scrolled in never shifts the main
 * stream — the run stays reproducible from its frames alone.
 */
export function advanceDescentTo(state: GameState, newOffset: number): GameState {
  if (state.status === 'game-over' || !(newOffset > state.descentOffset)) return state;

  const board = revealCeilingRows(state.board, state.seed, newOffset);
  const overflow = checkGameOver(board, newOffset);
  const newStatus: GameStatus = overflow ? 'game-over' : state.status;

  return {
    ...state,
    board,
    descentOffset: newOffset,
    status: newStatus,
    endReason: overflow ? 'overflow' : state.endReason,
  };
}

/**
 * Generate the rows a scroll to `offset` reveals above the ceiling (plus the
 * two buffer rows). Existing bubbles NEVER move — only the visual offset
 * scrolls.
 */
function revealCeilingRows(board: BoardState, seed: string, offset: number): BoardState {
  const rowsScrolled = Math.floor(offset / ROW_HEIGHT_PX);
  const targetCeiling = -(rowsScrolled + 2); // 2 buffer rows above viewport

  let newCeiling = board.ceilingRow;
  while (newCeiling > targetCeiling) {
    newCeiling--;
    const rowRng = createRNG(`${seed}:row:${newCeiling}`);
    for (let q = 0; q <= board.width; q++) {
      board = addBubble(board, { q, r: newCeiling }, rowRng.pick(board.activeColors));
    }
  }

  return newCeiling !== board.ceilingRow ? { ...board, ceilingRow: newCeiling } : board;
}

/**
 * Auto-apply a draft's harmful card, then drop anything it left floating.
 *
 * Row Inject is a descent jump, not a board edit: the tower scrolls down one
 * full row and the ceiling reveals a fresh one. Shifting every gear's `r` by
 * one instead flips its odd-r parity, which slides each row half a cell
 * sideways and severs every diagonal link — gears that were visibly hanging
 * from the tower came crashing down. Exported for tests.
 */
export function applyHarmfulCard(
  board: BoardState,
  descentOffset: number,
  card: MutationCard,
  state: GameState,
): { board: BoardState; descentOffset: number } {
  if (card.type === MutationType.RowInject) {
    const newOffset = descentOffset + ROW_HEIGHT_PX;
    return { board: revealCeilingRows(board, state.seed, newOffset), descentOffset: newOffset };
  }
  board = applyMutation(board, card, state.rng);
  const floating = findFloatingClusters(board);
  if (floating.length > 0) board = removeBubbles(board, floating);
  return { board, descentOffset };
}

/** Interval between Rogue theft events. */
export const THEFT_INTERVAL_MS = 45000;

/**
 * Pick a random visible non-obstacle gear from the board for the Rogue to
 * steal. Returns null when no valid target exists (empty board, only obstacles,
 * or every gear is scrolled out of view).
 */
export function pickTheftTarget(
  board: BoardState,
  rng: PRNG,
  descentOffset: number,
): HexCoord | null {
  const candidates: HexCoord[] = [];
  for (const [, bubble] of board.bubbles) {
    if (bubble.isObstacle) continue;
    const visualY = descentOffset + (bubble.coord.r - 1) * ROW_HEIGHT_PX;
    if (visualY >= 0 && visualY <= CONFIG.BOARD_HEIGHT_PX) {
      candidates.push(bubble.coord);
    }
  }
  if (candidates.length === 0) return null;
  return rng.pick(candidates);
}

export interface TheftResult {
  readonly state: GameState;
  readonly stolenCoord: HexCoord;
  readonly droppedCoords: HexCoord[];
}

/**
 * Apply a Rogue theft: remove the target gear and any gears that become
 * floating as a result. Returns null if the target is no longer present (e.g.
 * the player popped it mid-approach). Updates `lastTheftAt` so the 45s
 * cooldown restarts.
 */
export function applyRogueTheft(
  state: GameState,
  coord: HexCoord,
  now: number,
): TheftResult | null {
  const target = getBubble(state.board, coord);
  if (!target || target.isObstacle) return null;

  let board = removeBubbles(state.board, [coord]);
  const floating = findFloatingClusters(board);
  if (floating.length > 0) {
    board = removeBubbles(board, floating);
  }

  return {
    state: { ...state, board, lastTheftAt: now },
    stolenCoord: coord,
    droppedCoords: floating,
  };
}

/**
 * Compute a deterministic checksum of the game state for verification.
 *
 * Uses a simple hash of the key state fields to detect drift between clients.
 */
export function checksumState(state: GameState): number {
  let hash = 0;

  // Hash turnNumber
  hash = (hash * 31 + state.turnNumber) | 0;

  // Hash score
  hash = (hash * 31 + state.score) | 0;

  // Hash multiplier
  hash = (hash * 31 + state.multiplierX10) | 0;

  // Hash bubble count
  hash = (hash * 31 + state.board.bubbles.size) | 0;

  // Hash each bubble's position and color (sorted by key for determinism)
  const sortedKeys = [...state.board.bubbles.keys()].sort();
  for (const key of sortedKeys) {
    const bubble = state.board.bubbles.get(key)!;
    hash = (hash * 31 + bubble.coord.q) | 0;
    hash = (hash * 31 + bubble.coord.r) | 0;
    // Hash color by indexing into enum
    const colorIndex = ALL_BUBBLE_COLORS.indexOf(bubble.color);
    hash = (hash * 31 + colorIndex) | 0;
  }

  // Hash active mutations count
  hash = (hash * 31 + state.activeMutations.length) | 0;

  // Hash status
  const statusMap: Record<string, number> = { 'aiming': 0, 'drafting': 1, 'game-over': 2 };
  hash = (hash * 31 + (statusMap[state.status] ?? 0)) | 0;

  return hash;
}
